import { isDifferential } from "@corca/core";
import { itemRootNode, withLabels, withoutLabels } from "@corca/data/libraries/item";

import type { SuggestionItem, SuggestionNodeType } from "@/components/suggestions/types";
import type { NodeJson, SuggestionLabelJson } from "@corca/data";

/**
 * The one node a library item inserts, when it inserts exactly one; a multi-root item has none
 * and behaves as an expression.
 */
export function libraryRootNode(item: SuggestionItem): NodeJson | undefined {
  const { nodesJson } = item;
  return item.type === "library" && nodesJson ? itemRootNode({ nodesJson }) : undefined;
}

/**
 * The kind of node a suggestion inserts: the item's type, or the root node's type for a
 * single-root library item, since a library item carries nodes rather than a node type.
 */
export function insertedNodeType(item: SuggestionItem): SuggestionNodeType {
  const root = libraryRootNode(item);
  return root ? (root.type as SuggestionNodeType) : item.type;
}

/**
 * The props of the node a suggestion inserts: the root node's for a library item, the item's
 * own otherwise.
 */
export function insertedNodeProps(item: SuggestionItem): Record<string, unknown> | undefined {
  const root = libraryRootNode(item);
  return (root ? root.props : item.props) as Record<string, unknown> | undefined;
}

/**
 * Whether a suggestion inserts a differential function, read off the root node's key for a
 * library item and the item's flag for a typed one.
 */
export function insertsDifferential(item: SuggestionItem): boolean {
  const root = libraryRootNode(item);
  if (root) return isDifferential(root);
  return item.type === "function" && !!(item as SuggestionItem<"function">).isDifferential;
}

/**
 * The index labels a suggestion carries: a library item's on its root symbol, a typed item's in
 * `labels`; each as the number it holds when it is a single number, `null` otherwise.
 */
export function indexLabels(item: SuggestionItem): (string | null)[] {
  const root = libraryRootNode(item);
  if (root) {
    const symbol = root.children.find((child) => child.type === "symbol");
    return (symbol?.children ?? [])
      .filter((child) => child.type === "label")
      .map((label) => {
        const [only] = label.children[0]?.children ?? [];
        const value = only?.type === "number" ? only.props.value : undefined;
        return label.children[0]?.children.length === 1 && typeof value === "string" ? value : null;
      });
  }
  return (item.labels ?? []).map((label) =>
    label.nodes.length === 1 && label.nodes[0].type === "number"
      ? (label.nodes[0].value ?? null)
      : null,
  );
}

/**
 * The suggestion item with one index label, `x2` as x₂: on the nodes for a library item, in
 * `labels` for a typed one.
 */
export function withIndexLabel(item: SuggestionItem, value: string): SuggestionItem {
  const labels: SuggestionLabelJson[] = [
    { position: "right-bottom", nodes: [{ type: "number", value }] },
  ];
  if (item.type === "library" && item.nodesJson) {
    return { ...item, nodesJson: withLabels(item.nodesJson, labels) };
  }
  return { ...item, labels };
}

/**
 * The suggestion item without its labels, so a power reads the base symbol.
 */
export function withoutItemLabels(item: SuggestionItem): SuggestionItem {
  if (item.type === "library" && item.nodesJson) {
    return { ...item, nodesJson: withoutLabels(item.nodesJson) };
  }
  return { ...item, labels: undefined };
}
